/**
 * The extraction pane: what was extracted from this page, in the order it is
 * worth reading (§10.4).
 *
 *   1. Tables      — the printed shape, for comparing against the image
 *   2. Narratives  — heading and body, with the printed ordinal
 *   3. Typed facts — collapsed; the per-cell audit view
 *   4. Checks      — failing first, each expandable to its detail
 *   5. Extraction  — model, prompt, attempt, tokens, latency; collapsed
 *
 * Only what exists is rendered. A page that produced nothing says so, and
 * says why, because that is a finding rather than a gap in this screen.
 */
import { ChevronRight } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { PagePayload } from '@server/sql/page.ts'
import type { RailPage as Rail } from '@server/sql/documents.ts'
import { Badge, Field, Label, Panel } from '~/components/ui/primitives.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { EmptyState } from '~/components/ui/empty.tsx'
import { Raw, Untranslated } from '~/components/ui/raw.tsx'
import { useT } from '~/i18n/index.tsx'
import { count, percent0 } from '~/i18n/format.ts'
import { LogicalTableView } from './table.tsx'
import { FactAudit } from './facts.tsx'
import { cn } from '~/lib/cn.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'

export function ExtractionPane({
  payload, rail, symbol, documentId,
}: {
  payload: PagePayload
  rail: Rail | undefined
  /** Both only so a resolved note reference can link to the page it is on. */
  symbol: string
  documentId: number
}) {
  const { t, tn, v } = useT()
  const superadmin = useSuperadmin()
  const { page, tables, sections, facts, extraction, auditors, kam } = payload

  const nothing =
    tables.length === 0 && sections.length === 0 && facts.length === 0 &&
    auditors.length === 0 && kam.length === 0
  const skipped = page.extractor === 'skip'
  const pageTypeLabel = v('pageType', page.pageType) ?? page.pageType ?? '—'

  return (
    <div className="flex h-full min-w-0 flex-col bg-[var(--bg)]">
      {/* --- page header ----------------------------------------------- */}
      <header className="border-b border-[var(--border)] px-3 py-2">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h2 className="text-[length:var(--text-title)] font-medium">
            {t('viewer.page', { n: page.pageNo })}
          </h2>
          <span className="text-[length:var(--text-data)] text-[var(--text-muted)]">
            {v('pageType', page.pageType) ?? <Untranslated>{page.pageType ?? '—'}</Untranslated>}
          </span>
          <div className="flex-1" />
          {page.tier && <Badge state="neutral">{t('viewer.tier')} {page.tier}</Badge>}
          {page.extractor && (
            <Badge state="neutral">{v('extractor', page.extractor) ?? page.extractor}</Badge>
          )}
          {extraction && (
            <Badge state={extraction.ok ? 'pass' : 'fail'}>
              {extraction.ok ? 'ok' : 'failed'}
            </Badge>
          )}
          {extraction?.repaired && <Badge state="warn">{t('viewer.repaired')}</Badge>}
          {(extraction?.escalations ?? 0) > 0 && (
            <Badge state="warn">{t('viewer.escalations')} {extraction!.escalations}</Badge>
          )}
        </div>

        {rail && (
          <div className="tnum mt-1 flex flex-wrap items-center gap-x-2 text-[length:var(--text-micro)] text-[var(--text-faint)]">
            <span>{tn('common.facts', rail.extracted.facts, { n: rail.extracted.facts })}</span>
            <Sep />
            <span>{tn('viewer.tableCount', tables.length, { n: tables.length })}</span>
            <Sep />
            {/* "all 0 pass" is not a reassurance, it is a category error:
                a page with no checks was not checked. */}
            <span className={superadmin && rail.checks.failed > 0 ? 'text-[var(--fail)]' : undefined}>
              {!superadmin
                ? null
                : rail.checks.total === 0
                ? t('viewer.noChecks')
                : rail.checks.failed > 0
                  ? t('viewer.checksFailed', { n: rail.checks.failed })
                  : t('viewer.checksAllPass', { n: rail.checks.total })}
            </span>
            {page.flags.length > 0 && (
              <>
                <Sep />
                {page.flags.map((f) => <Untranslated key={f}>{f}</Untranslated>)}
              </>
            )}
          </div>
        )}

        <PageSource source={page.source} />
      </header>

      {/* --- body -------------------------------------------------------- */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-3 py-3">
        {nothing && (
          <EmptyState
            title={skipped ? t('viewer.skippedPage') : t('viewer.nothingExtracted')}
          >
            {skipped
              ? t('viewer.skippedPageWhy', { pageType: pageTypeLabel })
              : t('viewer.nothingExtractedWhy', { pageType: pageTypeLabel })}
          </EmptyState>
        )}

        {tables.length > 0 && (
          <section>
            <Label className="mb-1.5">{t('viewer.tables')}</Label>
            <div className="space-y-3">
              {tables.map((tb) => (
                <LogicalTableView key={tb.id} table={tb} symbol={symbol} documentId={documentId} />
              ))}
            </div>
          </section>
        )}

        {sections.length > 0 && (
          <section>
            <Label className="mb-1.5">{t('viewer.narratives')}</Label>
            <div className="space-y-2">
              {sections.map((n) => <SectionView key={n.id} section={n} pageNo={page.pageNo} />)}
            </div>
          </section>
        )}

        {(auditors.length > 0 || kam.length > 0) && (
          <section className="space-y-2">
            {auditors.length > 0 && (
              <>
                <Label>{t('viewer.auditors')}</Label>
                {auditors.map((a) => <RecordPanel key={String(a.id)} record={a} lang={page.lang} />)}
              </>
            )}
            {kam.length > 0 && (
              <>
                <Label>{t('viewer.kam')}</Label>
                {kam.map((k) => <RecordPanel key={String(k.id)} record={k} lang={page.lang} />)}
              </>
            )}
          </section>
        )}

        {facts.length > 0 && (
          <section>
            <Disclosure
              tone="strong"
              summary={`${t('viewer.factsTyped')} — ${t('viewer.factsHint')}`}
              count={facts.length}
            >
              <FactAudit facts={facts} />
            </Disclosure>
          </section>
        )}

        {/* The per-page check list used to sit here. Removed on request: this
            pane is for reading what was extracted, and adjudicating a failing
            check is a different task with a screen of its own. The checks
            themselves are untouched -- the rail still colours each page by
            its worst result, shift+arrow still jumps to the next failing
            page, and the company's Quality tab still lists every check down
            to the individual reading. */}

        {/* The model, prompt version, token counts and latency used to sit
            here as a collapsed panel. Removed on request: this pane is for
            judging the extraction against the page, and the machinery that
            produced it is a different question. What survives is the part
            that is about *this page's* result rather than about the model --
            a failure and any region it could not read. The full per-call
            record is still one keystroke away under `r`. */}
        {extraction?.error && (
          <section>
            <div className="rounded-[var(--radius-control)] border border-[var(--fail)]/25 bg-[var(--fail-bg)] px-2.5 py-2">
              <Label>{t('common.error')}</Label>
              <div
                dir="ltr"
                className="mt-0.5 font-[family-name:var(--font-mono)] text-[length:var(--text-tiny)] text-[var(--fail)]"
              >
                {extraction.error}
              </div>
            </div>
          </section>
        )}

        {Array.isArray(extraction?.illegible) && extraction.illegible.length > 0 && (
          <section>
            <Disclosure summary={t('viewer.illegible')} count={extraction.illegible.length}>
              <pre
                dir="ltr"
                className="overflow-x-auto overscroll-x-contain rounded-[2px] bg-[var(--bg-sunken)] px-2 py-1.5 font-[family-name:var(--font-mono)] text-[length:var(--text-micro)] leading-[var(--text-micro--line-height)] text-[var(--text-muted)]"
              >
                {JSON.stringify(extraction.illegible, null, 2)}
              </pre>
            </Disclosure>
          </section>
        )}
      </div>
    </div>
  )
}

/**
 * How the page was printed, on one line.
 *
 * This is about the paper, not about the model: a page that arrived as a
 * 200 dpi scan with no text layer is a page where a misread digit has an
 * obvious explanation, and that is worth knowing while judging the numbers
 * next to it. Only the facts that change how you read the page are here --
 * the full probe blob stays in the database.
 */
function PageSource({ source }: { source: PagePayload['page']['source'] }) {
  const { t, locale } = useT()

  const parts: React.ReactNode[] = []

  if (source.scanned) {
    parts.push(
      <span key="scan" className="text-[var(--warn)]">
        {t('viewer.source.scan')}
        {source.dpi ? ` · ${t('viewer.source.dpi', { n: source.dpi })}` : ''}
      </span>,
    )
  }
  // Zero characters on a page full of numbers is the single most useful
  // thing this line says.
  if (source.charCount === 0) {
    parts.push(<span key="notext" className="text-[var(--warn)]">{t('viewer.source.noText')}</span>)
  } else if (source.charCount !== null) {
    parts.push(<span key="chars">{t('viewer.source.chars', { n: count(source.charCount, locale) })}</span>)
  }
  if (source.rotation) {
    parts.push(<span key="rot">{t('viewer.source.rotated', { n: source.rotation })}</span>)
  }
  if (source.ink !== null) {
    parts.push(<span key="ink">{t('viewer.source.ink', { pct: percent0(source.ink, locale) })}</span>)
  }
  // Phase 2's separator evidence is only interesting when it disagreed with
  // itself; a clean count is noise.
  if (source.separator?.suspectReversed) {
    parts.push(
      <span key="sep" className="text-[var(--warn)]">
        {t('viewer.source.reversedDigits', { n: source.separator.suspectReversed })}
      </span>,
    )
  }
  if (source.renderW && source.renderH) {
    parts.push(
      <span key="px" dir="ltr" className="tnum">{source.renderW}×{source.renderH}</span>,
    )
  }

  if (parts.length === 0) return null

  return (
    <div className="tnum mt-0.5 flex flex-wrap items-center gap-x-2 text-[length:var(--text-micro)] text-[var(--text-faint)]">
      {parts.map((node, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <Sep />}
          {node}
        </span>
      ))}
    </div>
  )
}

type Section = PagePayload['sections'][number]

/**
 * One stitched section: heading, prose, numbered articles where the document
 * has them, and -- for a section whose content is structured rather than
 * prose, like an auditor's report -- the payload fields that no typed table
 * carries.
 */
function SectionView({ section: n, pageNo }: { section: Section; pageNo: number }) {
  const { t, tn, v } = useT()
  const articles = Array.isArray(n.articles) ? (n.articles as Record<string, unknown>[]) : []

  // Everything the structured payload holds that is not already a column on
  // auditors / key_audit_matters, which are rendered above in full.
  const COVERED = new Set(['firms', 'is_joint', 'opinion_type', 'key_audit_matters',
    'report_date_hijri_raw', 'report_date_gregorian_raw', 'blocks', 'tables', 'rows'])
  const extra = Object.entries(n.payload ?? {}).filter(
    ([k, val]) => !COVERED.has(k) && val !== null && val !== '' &&
      !(Array.isArray(val) && val.length === 0),
  )

  return (
    <Panel className="px-3 py-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {n.ordinalRaw && (
          <Raw lang={n.lang} className="shrink-0 font-medium text-[var(--text-muted)]">
            {n.ordinalRaw}
          </Raw>
        )}
        {n.headingRaw ? (
          <Raw as="h3" lang={n.lang} className="text-[length:var(--text-prose)] font-medium text-[var(--text)]">
            {n.headingRaw}
          </Raw>
        ) : (
          <span className="text-[length:var(--text-data)] text-[var(--text-muted)]">
            {v('extractor', n.kind) ?? <Untranslated>{n.kind ?? '—'}</Untranslated>}
          </span>
        )}
        <div className="flex-1" />
        {/* A section printed across a page break belongs to both pages. Saying
            so is what stops "half the paragraph is missing" reading as a bug. */}
        {n.pageNos.length > 1 && (
          <Badge state="neutral" dir="ltr" title={t('viewer.page', { n: pageNo })}>
            {t('common.pages', { n: n.pageNos.join(', ') })}
          </Badge>
        )}
        {n.flags.map((f) => <Badge key={f} state="neutral" dir="ltr">{f}</Badge>)}
      </div>

      {n.bodyRaw && <NarrativeBody body={n.bodyRaw} lang={n.lang} />}

      {articles.length > 0 && (
        <ol className="mt-1.5 space-y-1.5">
          {articles.map((a, i) => (
            <li key={i} className="flex gap-2">
              <span className="tnum shrink-0 text-[length:var(--text-micro)] text-[var(--text-faint)]">
                {String((a.article_no as number) ?? i + 1)}
              </span>
              <Raw lang={n.lang} className="text-[length:var(--text-prose)] leading-[var(--text-prose--line-height)]">
                {(a.body_raw as string) ?? (a.text_raw as string) ?? JSON.stringify(a)}
              </Raw>
            </li>
          ))}
        </ol>
      )}

      {extra.length > 0 && (
        <div className="mt-1.5">
          <Disclosure summary={tn('common.rows', extra.length, { n: extra.length })}>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {extra.map(([k, val]) => (
                <Field key={k} label={k.replace(/_raw$/, '').replace(/_/g, ' ')}>
                  {typeof val === 'string' ? (
                    <Raw lang={n.lang} className="whitespace-pre-wrap">{val}</Raw>
                  ) : (
                    <pre dir="ltr" className="overflow-x-auto overscroll-x-contain font-[family-name:var(--font-mono)] text-[length:var(--text-micro)]">
                      {JSON.stringify(val, null, 2)}
                    </pre>
                  )}
                </Field>
              ))}
            </div>
          </Disclosure>
        </div>
      )}
    </Panel>
  )
}

/**
 * A section body, clamped rather than cut.
 *
 * The previous version sliced the string at 700 characters and rendered the
 * two halves as separate paragraphs with the toggle between them. That is
 * wrong twice over: the cut lands mid-word -- «ريال سعودي» became «ري…» and
 * «ال سعودي» -- and expanding gave two blocks with a control wedged in the
 * middle rather than one continuous passage.
 *
 * So the text is never split. One element holds the whole body; collapsing
 * limits its *height* and fades the last line out with a mask, which needs no
 * knowledge of the background and so works in both themes. Expanding removes
 * the limit. The full text is always in the DOM, which is also what keeps
 * ctrl-F working on a collapsed section.
 */
function NarrativeBody({ body, lang }: { body: string; lang: string | null }) {
  const { t } = useT()
  const ref = useRef<HTMLElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [clamped, setClamped] = useState(false)

  // A new page means a new body: start collapsed again.
  useEffect(() => setExpanded(false), [body])

  // The clamp is applied first and measured second, never the other way
  // round: an unclamped element has scrollHeight === clientHeight, so
  // measuring before clamping always reports "nothing hidden" and the clamp
  // never engages.
  const collapsed = !expanded

  // Whether the clamp actually hides anything is then a measurement, not a
  // character count -- the pane is resizable, and a body that overflows at
  // one width fits at another.
  useLayoutEffect(() => {
    const el = ref.current
    // Skip while expanded: scrollHeight equals clientHeight then, which would
    // read as "nothing hidden" and make the collapse control disappear.
    if (!el || expanded) return
    const check = () => setClamped(el.scrollHeight > el.clientHeight + 4)
    check()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [body, expanded])

  return (
    <>
      <Raw
        ref={ref}
        as="p"
        lang={lang}
        className={cn(
          'mt-1 text-[length:var(--text-prose)] leading-[var(--text-prose--line-height)]',
          collapsed && 'max-h-[10.5rem] overflow-hidden',
          // The fade is the signal that there is more; a body that fits
          // exactly should not look cut off.
          collapsed && clamped && '[mask-image:linear-gradient(to_bottom,black_65%,transparent)]',
        )}
      >
        {body}
      </Raw>
      {clamped && (
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          aria-expanded={expanded}
          className="mt-1 inline-flex items-center gap-1 text-[length:var(--text-micro)] link-inline"
        >
          <ChevronRight
            size={13}
            aria-hidden="true"
            className={cn('transition-transform duration-100 rtl:-scale-x-100', expanded && 'rotate-90')}
          />
          {expanded ? t('action.showLess') : t('action.showMore')}
        </button>
      )}
    </>
  )
}

/**
 * An auditor or KAM row. Rendered generically from its columns rather than
 * field by field: these tables gain columns as the pipeline learns to read
 * more, and a hand-written list would silently drop them.
 */
function RecordPanel({ record, lang }: { record: Record<string, unknown>; lang: string | null }) {
  const skip = new Set(['id', 'document_id', 'page_id', 'created_at', 'extra'])
  // `extra` is flattened in rather than skipped: on auditors it carries
  // `addressee_raw` and `emphasis_of_matter`, which exist nowhere else and
  // were being dropped on the floor.
  const flat: Record<string, unknown> = {
    ...record,
    ...(record.extra && typeof record.extra === 'object' && !Array.isArray(record.extra)
      ? (record.extra as Record<string, unknown>)
      : {}),
  }
  const entries = Object.entries(flat).filter(
    ([k, v]) => !skip.has(k) && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0),
  )
  return (
    <Panel className="grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-2.5 sm:grid-cols-3">
      {entries.map(([k, v]) => (
        <Field key={k} label={k.replace(/_raw$/, '').replace(/_/g, ' ')}>
          {typeof v === 'string' ? (
            <Raw lang={lang} className="whitespace-pre-wrap">{v}</Raw>
          ) : (
            <span className="tnum">{String(v)}</span>
          )}
        </Field>
      ))}
    </Panel>
  )
}

const Sep = () => <span className="text-[var(--border-strong)]">·</span>
